import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleManager } from 'src/shared/models/module-manager';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { MetricReviewService } from './metric-review.service';
import { MetricReviewData } from './metric-review.models';
import { GridOptions } from 'ag-grid-community';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { AgGridAngular } from 'ag-grid-angular';


@Component({
    selector: 'cv-metric-review',
    templateUrl: './metric-review.component.html',
    styleUrls: ['./metric-review.component.scss']
})
export class MetricReviewComponent implements OnInit {

    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    @ViewChild('metricReviewWidgetGrid') agGrid: AgGridAngular;

    data: MetricReviewData = null;

    agGridLocalizationStrings = null; // Stores key/value pairs of strings used within agGrid for localization.
    fetchPromise = null; // The promise from fetchModuleData is stored so pending requests can be canceled if settings change.
    isFirstLoad = true; // Used for logging in endpoint
    isRequestingLocalizations = false;
    loadingStates = { config: true, data: true, localizations: true }; // Tracks loading for spinner.  Update through setLoadingState()
    localizationStrings = null;
    sortModel = null; // The sorted column state of the ag-grid.
    stateSaveTimer = null; // Used to debounce the save states
    stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"

    // These two variables are bounded versions of the same function names from the factory.
    fetchModuleData;
    fetchExportData;

    didLastFetchFail = false;
    gridOptions: GridOptions = null;
    tableState: any = null;
    isFetching = false;
    isFirstDataLoaded = false;
    isGridReady = false;
    isNoMetric = false;
    filterValue = "";
    showFilter = false;
    searchPlaceholder = "";

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting };
    private isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: MetricReviewService,
        private configService: CvConfigurationService
    ) { }

    async ngOnInit() {
        await this.loadLocalizations();
        this.fetchModuleData = this.service.fetchModuleData.bind(this.service, this.moduleKey);
        this.fetchExportData = this.service.fetchExportData.bind(this.service, this.moduleKey);
        this.configureAgGrid();

        this.moduleManager.setOptions({
            onSettingsApplied: (settings) => {
                this.setLoadingState("config", false);
                this.setLoadingState("data", true);
                this.abortGetData();
                this.getData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Default,
            onRefresh: (isAutoRefresh) => {
                if (!isAutoRefresh) {
                    this.setLoadingState("data", true);
                }
                this.getData(isAutoRefresh);
            },
            canRefresh: () =>  !this.isFetching && !this.isLoading(),
            onExport: format => this.fetchExportData(format),
        });

        this.fetchModuleData = this.service.fetchModuleData.bind(this.service, this.moduleKey);
        this.fetchExportData = this.service.fetchExportData.bind(this.service, this.moduleKey);

        this.configureAgGrid();

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch()
        });

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                if (!stateData) { // No table state loaded
                    return;
                }
                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true
        });
    }

    setLoadingState(subState, value) {
        this.loadingStates[subState] = value;
    }

    private getData(isAutoRefresh) {
        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        if (!this.isFetching) {
            this.isFetching = true;
            this.fetchPromise = this.fetchModuleData({ isAutoRefresh, isFirstLoad: this.isFirstLoad });
            this.fetchPromise.then((data) => {
                this.isFirstLoad = false;
                this.fetchPromise = null;
                delete data["BackendMessages"];

                this.data = data.data;

                // Error 1 = no metric
                if (this.data.Error == 1) {
                    this.didLastFetchFail = false;
                    this.isFirstDataLoaded = true;
                    this.isNoMetric = true;
                    this.setLoadingState("data", false);
                    return;
                }

                this.isNoMetric = false;

                if (this.isGridReady && this.agGrid?.api)
                    this.agGrid?.api.setGridOption('rowData', this.data.Metrics);

                this.setLoadingState("data", false);
                this.didLastFetchFail = false;
                this.isFirstDataLoaded = true;
                this.moduleManager.setAutoRefreshReady();
            }).catch((err) => {
                console.error("Metric Review: Error fetching metric data. Status code from $http:",
                    err.status, "Status code from data:", err.data && err.data.status_code);
                this.didLastFetchFail = true; // Could be used to update the View.
                this.moduleManager.handleLoadError(err, isAutoRefresh);
            }).finally(() => {
                this.isFetching = false;
                this.moduleManager.showLoader = false;
            });
        }
    }

    // The primary scenario of abort: When this module's settings are changed.
    abortGetData() {
        if (this.isFetching) {
            this.fetchPromise.abort();
            this.isFetching = false;
        }
    }

    /**
     * Loads the localizations for this module, then sets up the grid.
     */
    private async loadLocalizations() {
        try {
            this.localizationStrings = await this.cvLocalizationService.GetSection("Metric_Review_Module");
            this.localizationStrings = await this.cvLocalizationService.GetSection("Common");
        } finally {
            this.setLoadingState("localizations", false);
            this.isRequestingLocalizations = false;
        }
    }

    /**
     * Returns the localization for the provided Key, or defaultValue if missing.
     * @param key String's Key from the *.Language Section for this module
     * @param defaultValue String returned if localizations didn't load, or the key is missing.
     */
    private getLocalizationString(key: string, defaultValue: string): string {
        if (this.localizationStrings?.has(key))
            return this.localizationStrings.get(key);
        return defaultValue;
    }

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.agGrid?.api || this.stateRestoreInProgress)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
        if (tableState != null) {
            this.tableState = tableState;
        }

        // tell module manager to save the state
        this.queueStateSave();
    };

    /**
     * This function debounces state saves to 500ms.
     */
    queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }

        this.stateSaveTimer = setTimeout(async () => {
            this.stateSaveTimer = false;
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    }

    restoreColumnState() {
        if (!this.tableState|| !this.agGrid?.api) {
            return;
        }

        this.agGridHelperService.loadGridState(this.agGrid?.api, this.tableState);
    };

    /**
     * This function is where the ag-grid gridOptions is configured.
     */
    public async configureAgGrid() {
        const localizedString = this.cvLocalizationService.getCachedText("Common", "Original_Metric", "Original Metric Name");
        // This function assumes the agGridLocalizationStrings and localizationStrings have been set.
        // Since the backend is returning the real Metric object, the abbreviation (MetricName if empty) is returned for the value of that cell.
        function metricNameValueGetter(params) {
            const metric = params.data.Metric;
            const abbr = metric.Abbreviation || "";
            if(metric.MetricCustomName && metric.MetricCustomName != "")
                return metric.MetricCustomName+"*";
            if (abbr.trim().length > 1)
                return abbr;
            else {
                return metric.MetricName;
            }
        }

        // Since the value represented in the Metric column can be the abbreviation, this tooltip getter returns the actual MetricName
        function metricNameTooltipGetter (params) {
            return params.data.Metric.MetricCustomName && params.data.Metric.MetricCustomName != "" 
                ? localizedString + ": " +'\n' + params.data.Metric.MetricName
                : params.data.Metric.MetricName;
        }

        this.gridOptions = this.agGridHelperService.createGridOptions({
            animateRows: true,
            context: {
                getMetric: params => params.data.Metric,
            },
            defaultColDef: {
                editable: false,
                minWidth: this.agGridHelperService.defaultMinWidth,
                resizable: true,
                sortable: true,
                tooltipValueGetter: (params) => {
                    return params.valueFormatted || params.value;
                },
                icons: this.configService.isCxOne ? {
                    sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                }: null,
            },
            colResizeDefault: "shift",
            columnDefs: [
                {
                    colId: "metric",
                    comparator: (valueA, valueB) => valueA.localeCompare(valueB, undefined, { sensitivity: "accent" }),
                    headerName: this.getLocalizationString(["Metric"][0], "Metric"),
                    tooltipValueGetter: metricNameTooltipGetter,
                    valueGetter: metricNameValueGetter,
                    filter: 'agTextColumnFilter',
                    suppressMenu: this.isSharedURL,
                },
                {
                    colId: "Today",
                    field: "Today",
                    headerName: this.getLocalizationString(["Today"][0], "Today"),
                    type: 'metricColumn',
                    suppressMenu: this.isSharedURL,
                },
                {
                    colId: "Week",
                    field: "Week",
                    headerName: this.getLocalizationString(["Last_7_Days"][0], "Last 7 Days"),
                    type: 'metricColumn',
                    suppressMenu: this.isSharedURL,
                },
                {
                    colId: "Month",
                    field: "Month",
                    headerName: this.getLocalizationString(["Last_30_Days"][0], "Last 30 Days"),
                    type: 'metricColumn',
                    suppressMenu: this.isSharedURL,
                },
            ],
            enableBrowserTooltips: true,
            localeText: this.agGridLocalizationStrings,
            // grid events
            onGridReady: (event: any) => {
                if (this.data)
                    event.api.setGridOption('rowData', this.data.Metrics);

                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

                this.restoreColumnState(); //Needed to restore columns size saved on the tablestate.
                if (!this.tableState) {
                    this.agGridHelperService.sizeColumnsSmart(event); //Needed to resize the grid when adding/removing metrics or doing any modifications that will reload the grid.
                    this.updateTableState();
                }

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save
            },
            onColumnVisible: () => this.updateTableState(),
            onFilterChanged: () => this.updateTableState(),
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    this.updateTableState();
                }
            },
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.updateTableState();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }
            },
            getRowId: ({data: data}) => data.Metric.MetricID,
            rowData: [],
            rowSelection: "multiple",
            suppressCellFocus: true,
            suppressContextMenu: true,
            suppressScrollOnNewData: true,
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
            overlayNoRowsTemplate: this.configService.isCxOne ?
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
        }, {
            enableCopy: true
        });

        if (!this.moduleManager.hasWriteAccess() && !this.configService.isCxOne) {
            this.agGridHelperService.setGridReadOnly(this.gridOptions);
        }

        this.isGridReady = true;
    }

    /**
     * If subState isn't specified, this function returns the cumulative result of all the loadingStates.  If any are loading, it returns true.
     * If subState is specified, only the loading state of that subState is returned.
     */
    isLoading(subState?) {
        if (!subState) {
            const loading = Object.keys(this.loadingStates).reduce((accumulator, subState) => accumulator || this.loadingStates[subState], false);
            return loading;
        } else {
            if (this.loadingStates[subState] !== undefined) {
                return this.loadingStates[subState];
            } else {
                return false;
            }
        }
    }

    toggleOpenSearch() {
        if (this.showFilter) {
            this.showFilter = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    };

    applyFilter() {
        if (this.agGrid?.api) {
            this.filterValue = this.filterValue;
            this.agGrid?.api?.setGridOption('quickFilterText', this.filterValue.trim());
            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    clearFilter(): void {
        this.filterValue = "";
        this.applyFilter();
    }

    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.configService.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            }
        }, 200);
    }
}

